import { create } from 'zustand'
import type { Order } from '@/lib/vision/schemas'
import {
  MAX_BULK_ACK_POS,
  isBulkAckEligible,
  normalizeAckChannel,
} from './bulk-ack-eligibility'

interface BulkAckSelectionState {
  orders: Order[]
  toggle: (order: Order) => void
  selectMany: (orders: readonly Order[]) => { added: number; capped: boolean }
  removeMany: (orderIds: readonly string[]) => void
  clear: () => void
  keepTenant: (tenantId: string) => void
  /** Drop the batch when the Amazon channel family filter changes. */
  clearIfChannelFilterChanged: (channels: readonly string[], channelCode: string | null) => void
}

/**
 * Memory-only bulk-ack selection. Survives paging inside the orders queue;
 * clears on tenant change and when the channel family / account filter changes.
 */
export const useBulkAckSelectionStore = create<BulkAckSelectionState>((set, get) => ({
  orders: [],
  toggle: order =>
    set(state => {
      if (state.orders.some(row => row.id === order.id)) {
        return { orders: state.orders.filter(row => row.id !== order.id) }
      }
      if (!isBulkAckEligible(order)) return state
      const sameTenant = state.orders.filter(row => row.tenantId === order.tenantId)
      const selectedChannel = sameTenant[0]?.channel
      if (
        selectedChannel &&
        normalizeAckChannel(selectedChannel) !== normalizeAckChannel(order.channel)
      ) {
        return { orders: sameTenant }
      }
      if (sameTenant.length >= MAX_BULK_ACK_POS) return { orders: sameTenant }
      return { orders: [...sameTenant, order] }
    }),
  selectMany: incoming => {
    let added = 0
    let capped = false
    set(state => {
      const tenantId = incoming.find(order => order.tenantId)?.tenantId
      if (!tenantId) return state
      let orders = state.orders.filter(order => order.tenantId === tenantId)
      for (const order of incoming) {
        if (order.tenantId !== tenantId || orders.some(row => row.id === order.id)) continue
        if (!isBulkAckEligible(order)) continue
        const selectedChannel = orders[0]?.channel
        if (
          selectedChannel &&
          normalizeAckChannel(selectedChannel) !== normalizeAckChannel(order.channel)
        ) {
          continue
        }
        if (orders.length >= MAX_BULK_ACK_POS) {
          capped = true
          break
        }
        orders = [...orders, order]
        added += 1
      }
      return { orders }
    })
    return { added, capped: capped || get().orders.length >= MAX_BULK_ACK_POS }
  },
  removeMany: orderIds =>
    set(state => {
      const removed = new Set(orderIds)
      return { orders: state.orders.filter(order => !removed.has(order.id)) }
    }),
  clear: () => set({ orders: [] }),
  keepTenant: tenantId =>
    set(state => ({
      orders: state.orders.filter(order => order.tenantId === tenantId),
    })),
  clearIfChannelFilterChanged: (channels, channelCode) =>
    set(state => {
      if (state.orders.length === 0) return state
      const selected = state.orders[0]!
      const selectedChannel = normalizeAckChannel(selected.channel)
      if (channelCode != null && channelCode !== '') {
        // Account-scoped filter is a different queue; drop the batch.
        return { orders: [] }
      }
      if (channels.length === 0) return state
      const normalizedFilters = channels.map(normalizeAckChannel)
      if (!normalizedFilters.includes(selectedChannel)) {
        return { orders: [] }
      }
      // Filter still includes the batch channel — keep selection.
      return state
    }),
}))
